import { drawing as d, geometry as g } from '@progress/kendo-drawing';
import { VisualBase } from './VisualBase';
import { MarkerPathMixin } from './MarkerPathMixin';
import { createSegment } from './utils';
import { deepExtend } from './../utils/deep-extend';
/**
 * Represents a polyline (multi-segment line) with marker capabilities.
 * Extends VisualBase and incorporates MarkerPathMixin functionality.
 */
export class Polyline extends VisualBase {
    /**
     * Creates a new Polyline instance.
     * @param options - Configuration options for the polyline including points and styling
     */
    constructor(options) {
        options = deepExtend({ points: [] }, options);
        super(options);
        /** @hidden */
        this._capMap = MarkerPathMixin._capMap;
        this.container = new d.Group();
        this._getPath = MarkerPathMixin._getPath.bind(this);
        this._normalizeMarkerOptions = MarkerPathMixin._normalizeMarkerOptions.bind(this);
        this._removeMarker = MarkerPathMixin._removeMarker.bind(this);
        this._createMarkers = MarkerPathMixin._createMarkers.bind(this);
        this._createMarker = MarkerPathMixin._createMarker.bind(this);
        this._positionMarker = MarkerPathMixin._positionMarker.bind(this);
        this._redrawMarker = MarkerPathMixin._redrawMarker.bind(this);
        this._redrawMarkers = MarkerPathMixin._redrawMarkers.bind(this);
        this._initPath();
        this._createMarkers();
        this._initAccessibility();
    }
    /**
     * Gets the drawing container that holds the polyline and its markers.
     * @returns The drawing group container
     */
    drawingContainer() {
        return this.container;
    }
    /**
     * Gets or sets the points that define the polyline path.
     * @param points Array of points to set for the polyline, or undefined to get current points
     * @returns The current points array when called as getter
     */
    points(points) {
        const options = this.options;
        if (points) {
            options.points = points;
            this._updatePath();
        }
        else {
            return options.points;
        }
    }
    /**
     * Redraws the polyline with updated options.
     * @param options - Updated configuration options
     */
    redraw(options) {
        if (options) {
            const points = options.points;
            super.redraw.call(this, options);
            if (points && this._pointsDiffer(points)) {
                this.points(points);
                this._redrawMarkers(true, options);
            }
            else {
                this._redrawMarkers(false, options);
            }
        }
    }
    /**
     * @hidden
     * Initializes the path drawing element.
     */
    _initPath() {
        const options = this.options;
        this.drawingElement = new d.Path({
            stroke: options.stroke,
            id: options.id
        });
        this._fill();
        this.container.append(this.drawingElement);
        if (options.points) {
            this._updatePath();
        }
    }
    /** @hidden */
    _pointsDiffer(points) {
        const currentPoints = this.options.points;
        let differ = currentPoints.length !== points.length;
        if (!differ) {
            for (let i = 0; i < points.length; i++) {
                if (currentPoints[i].x !== points[i].x || currentPoints[i].y !== points[i].y) {
                    differ = true;
                    break;
                }
            }
        }
        return differ;
    }
    /**
     * @hidden
     * Updates the path with current points.
     */
    _updatePath() {
        const drawingElement = this.drawingElement;
        const options = this.options;
        const points = options.points;
        if (options.cornerRadius > 0) {
            createRoundedPolyline(drawingElement, points, options.cornerRadius);
        }
        else {
            const segments = [];
            let point;
            for (let i = 0; i < points.length; i++) {
                point = points[i];
                segments.push(createSegment(point.x, point.y));
            }
            drawingElement.segments.elements(segments);
        }
    }
}
const EPS = 1e-6;
const KAPPA = 0.5522847498307936; // 4*(sqrt(2)-1)/3
const rightAngleTolerance = 0.05;
function dist(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
}
function dedupe(points, eps = EPS) {
    if (points.length === 0)
        return points;
    const out = [points[0]];
    for (let i = 1; i < points.length; i++) {
        if (dist(points[i], out[out.length - 1]) > eps) {
            out.push(points[i]);
        }
    }
    return out;
}
function createRoundedPolyline(path, points, cornerRadius) {
    const pts = dedupe(points);
    if (pts.length < 2) {
        return path;
    }
    path.moveTo(pts[0].x, pts[0].y);
    if (pts.length === 2) {
        path.lineTo(pts[1].x, pts[1].y);
        return path;
    }
    for (let i = 1; i < pts.length - 1; i++) {
        const prev = pts[i - 1];
        const curr = pts[i];
        const next = pts[i + 1];
        const v1 = { x: curr.x - prev.x, y: curr.y - prev.y };
        const v2 = { x: next.x - curr.x, y: next.y - curr.y };
        const len1 = Math.hypot(v1.x, v1.y);
        const len2 = Math.hypot(v2.x, v2.y);
        if (len1 < EPS || len2 < EPS) {
            path.lineTo(curr.x, curr.y);
            continue;
        }
        const cross = v1.x * v2.y - v1.y * v2.x;
        const dot = (v1.x * v2.x + v1.y * v2.y) / (len1 * len2);
        const isColinearSameDir = Math.abs(cross) < EPS && dot > 0.999;
        if (isColinearSameDir) {
            continue;
        }
        const r = Math.min(cornerRadius, len1 / 2, len2 / 2);
        if (r <= EPS) {
            path.lineTo(curr.x, curr.y);
            continue;
        }
        const u1 = { x: v1.x / len1, y: v1.y / len1 };
        const u2 = { x: v2.x / len2, y: v2.y / len2 };
        const p1 = { x: curr.x - u1.x * r, y: curr.y - u1.y * r };
        const p2 = { x: curr.x + u2.x * r, y: curr.y + u2.y * r };
        const isRightAngle = Math.abs(dot) <= rightAngleTolerance;
        path.lineTo(p1.x, p1.y);
        if (isRightAngle) {
            const k = KAPPA * r;
            const c1 = { x: p1.x + u1.x * k, y: p1.y + u1.y * k };
            const c2 = { x: p2.x - u2.x * k, y: p2.y - u2.y * k };
            path.curveTo(new g.Point(c1.x, c1.y), new g.Point(c2.x, c2.y), new g.Point(p2.x, p2.y));
        }
        else {
            path.curveTo(new g.Point(curr.x, curr.y), new g.Point(curr.x, curr.y), new g.Point(p2.x, p2.y));
        }
    }
    const last = pts[pts.length - 1];
    path.lineTo(last.x, last.y);
    return path;
}
